<template lang="pug">
  #sendmessage-group-register(v-cloak)
    .box
      .box-header.with-border.register-header
        .title
          span 宛先グループ情報登録

      .box-body.register-body
        FormInput(
          key-name="グループ名"
          required
          name="groupname"
          v-model="name"
          type="text"
          placeholder="グループ名"
          :maxlength="32"
          :validation-rules="{ required: true, max: 32 }"
          :styles="{ width: '340px' }")
        FormInputSendMessageGroupType(v-model="type" :planType="planType")

      .box-footer.with-border.register-footer
        ValidationAlert(v-model="showValidationAlert")
        .buttons
          button.btn.register-btn(@click="showConfirmRegisterModal" type="button") 登録
          button.btn.cancel-btn(@click="gotoSendMessageGroupList" type="button") キャンセル

    OkCancelModal(v-model="showRegisterModal" title="宛先グループ情報を登録しますか？" @ok="registerSendMessageGroup")
    OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoSendMessageGroupList")

    PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendMessageGroupListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";
import SendMessageGroupsService, { GroupType, SendMessageGroupsPostModel } from "@/commons/services/SendMessageGroups";
import { PlanType } from "@/commons/services/Plans";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInputSendMessageGroupType from "@/pc/components/forms/FormInputSendMessageGroupType.vue";

/**
 * 宛先グループ登録画面コンポーネント
 */
@Component({
  components: {
    FormInput,
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert,
    FormInputSendMessageGroupType
  },
  $_veeValidate: { validator: "new" }
})
export default class SendMessageGroupRegister extends Vue {
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** グループ名 */
  name: string = "";
  /** 種別 */
  type: GroupType = GroupType.UNKNOWN;

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    this.planType = userStore.facility.planType;

    return;
  }

  /**
   * ### 登録確認ダイアログを表示する
   *
   * - バリデーションを実施する
   * - エラーでなければ確認ダイアログを表示する
   */
  public async showConfirmRegisterModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 宛先グループを登録する
   *
   * - ローディングを表示
   * - 宛先グループサービスを使用して宛先グループを登録する
   * - 例外が無ければ成功ダイアログを表示する
   */
  public async registerSendMessageGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const group: SendMessageGroupsPostModel = this.createRegisterParam();
    await this.sendMessageGroupsService.post(userStore, group);

    // 宛先グループ一覧を再読み込みさせるようstoreを更新
    let sendMessageGroupList: SendMessageGroupListState = needRefreshesStore.sendMessageGroupList;
    sendMessageGroupList.sendMessageGroupList = true;
    needRefreshesStore.setSendMessageGroupList(sendMessageGroupList);
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 宛先グループ登録パラメータ作成
   */
  public createRegisterParam(): SendMessageGroupsPostModel {
    const group: SendMessageGroupsPostModel = {
      name: this.$stringUtil.convertYen(this.name),
      type: this.type,
      members: []
    };

    return group;
  }

  /**
   * ### 宛先グループ一覧画面に遷移する
   */
  public gotoSendMessageGroupList(): void {
    this.$router.push(PcUrl.SendMessageGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#sendmessage-group-register {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
